import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { mockApprovalService } from '@/services/mock/MockApprovalService';
import { ApprovalItem, ApprovalDetailData } from '@/types/domain/approval';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { notifyGlobal } from '@/context/NotificationContext';
import {
  ShieldCheck,
  Clock,
  Eye,
  Search,
  X,
  RotateCcw,
  RefreshCw,
  Check,
  XCircle,
  ExternalLink,
  User,
  Building2,
  Layers,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const ApprovalsListPage: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Simplified Executive Filter State
  const [activeTab, setActiveTab] = useState<string>('Pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Drawer Preview State
  const [selectedDetail, setSelectedDetail] = useState<ApprovalDetailData | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);

  // Quick Reject Modal State
  const [rejectTarget, setRejectTarget] = useState<ApprovalItem | ApprovalDetailData | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Dynamic Queue Counts
  const [counts, setCounts] = useState<{ pending: number; approved: number; all: number }>({
    pending: 0,
    approved: 0,
    all: 0,
  });

  // 100ms Debounce for instant interactive search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 100);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [queueRes, allRes] = await Promise.all([
        mockApprovalService.getApprovalQueue({
          status: activeTab as any,
          search: debouncedSearch,
          page: 1,
          pageSize: 100,
        }),
        mockApprovalService.getApprovalQueue({
          page: 1,
          pageSize: 500,
        }),
      ]);

      setItems(queueRes.items);

      const pendingCount = allRes.items.filter((i) => i.status === 'Pending').length;
      const approvedCount = allRes.items.filter((i) => i.status === 'Approved').length;
      setCounts({
        pending: pendingCount,
        approved: approvedCount,
        all: allRes.items.length,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab, debouncedSearch]);

  const isFiltered = activeTab !== 'Pending' || debouncedSearch !== '';

  const handleClearFilters = () => {
    setActiveTab('Pending');
    setSearchQuery('');
    setDebouncedSearch('');
  };

  // Quick Action Handlers
  const handleQuickApprove = async (item: ApprovalItem | ApprovalDetailData, explicitStep?: number) => {
    try {
      setIsProcessingAction(true);
      const isStep1Pending = !item.workflowChain || item.workflowChain.length === 0 || item.workflowChain[0]?.status !== 'Completed';
      const stepToApprove = explicitStep !== undefined ? explicitStep : (isStep1Pending ? 1 : 2);

      if (stepToApprove === 1) {
        await mockApprovalService.approveStep(item.id, 1, 'Departmental operational requirement signoff granted.', 'Department Head');
      } else {
        await mockApprovalService.approveStep(item.id, 2, 'Cost center budget verified and financial clearance granted.', 'Finance Controller');
      }

      if (isDrawerOpen && selectedDetail?.id === item.id) {
        setIsDrawerOpen(false);
      }
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to approve requisition');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleOpenRejectModal = (item: ApprovalItem | ApprovalDetailData) => {
    setRejectTarget(item);
    setRejectReason('');
    setIsRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      alert('Please provide a mandatory reason for rejection.');
      return;
    }

    try {
      setIsProcessingAction(true);
      await mockApprovalService.rejectRequest(rejectTarget.id, rejectReason);
      notifyGlobal(
        'Requisition Rejected',
        `Requisition ${rejectTarget.relatedEntityId || rejectTarget.approvalNumber} has been rejected.`,
        'StatusChange',
        rejectTarget.id
      );
      setIsRejectModalOpen(false);
      setRejectTarget(null);
      setRejectReason('');
      if (isDrawerOpen && selectedDetail?.id === rejectTarget.id) {
        setIsDrawerOpen(false);
      }
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to reject requisition');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleOpenDrawer = async (id: string) => {
    try {
      setIsDrawerLoading(true);
      setIsDrawerOpen(true);
      const detail = await mockApprovalService.getApprovalDetailById(id);
      setSelectedDetail(detail);
    } finally {
      setIsDrawerLoading(false);
    }
  };

  // Squeezed Executive Tabs with Live Badges
  const TABS = [
    { label: `Needs My Approval (${counts.pending})`, value: 'Pending' },
    { label: `Approved by Me (${counts.approved})`, value: 'Approved' },
    { label: `All Queue Records (${counts.all})`, value: 'ALL' },
  ];

  const columns: ColumnDef<ApprovalItem>[] = [
    {
      header: 'REQ Number',
      accessorKey: 'relatedEntityId',
      cell: (row) => (
        <div className="font-poppins space-y-0.5">
          <button
            type="button"
            onClick={() => handleOpenDrawer(row.id)}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap block text-left"
          >
            {row.relatedEntityId || row.approvalNumber}
          </button>
          <span className="text-[10px] text-foreground-muted block font-mono">
            Task: {row.approvalNumber}
          </span>
        </div>
      ),
    },
    {
      header: 'Title & Category',
      accessorKey: 'title',
      cell: (row) => (
        <div className="font-poppins min-w-0 max-w-[190px] xl:max-w-[220px] space-y-1">
          <span className="font-bold text-foreground text-xs block truncate" title={row.title}>
            {row.title}
          </span>
          <Badge variant="outline" className="text-[9px] font-bold uppercase px-1.5 py-0">
            {row.approvalType.replace('_', ' ')}
          </Badge>
        </div>
      ),
    },
    {
      header: 'Requester & Dept',
      accessorKey: 'requesterName',
      cell: (row) => (
        <div className="font-poppins text-xs space-y-0.5 max-w-[130px] xl:max-w-[160px] truncate">
          <span className="font-bold text-foreground block truncate">{row.requesterName}</span>
          <span className="text-[11px] text-foreground-secondary block truncate" title={row.departmentName}>
            {row.departmentName}
          </span>
        </div>
      ),
    },
    {
      header: 'Estimated Value',
      accessorKey: 'totalAmount',
      cell: (row) => (
        <span className="font-poppins font-bold text-foreground font-mono whitespace-nowrap">
          ₹{row.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      header: 'SLA Status',
      accessorKey: 'slaHoursRemaining',
      cell: (row) => {
        const isOverdue = row.slaHoursRemaining < 0;
        const isDueSoon = row.slaHoursRemaining >= 0 && row.slaHoursRemaining <= 24;

        return (
          <div className="font-poppins text-xs space-y-0.5">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${
                isOverdue
                  ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20'
                  : isDueSoon
                  ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20'
                  : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
              }`}
            >
              <Clock className="h-3 w-3" />
              {isOverdue ? `Overdue (${Math.abs(row.slaHoursRemaining)}h)` : `${row.slaHoursRemaining}h left`}
            </span>
            <span className="text-[10px] text-foreground-muted block font-medium truncate max-w-[120px]">
              {row.currentStage}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center">
          <StatusBadge
            status={row.status}
            category="PurchaseRequest"
            className="w-32 py-1 justify-center rounded-full font-poppins"
          />
        </div>
      ),
    },
    {
      header: 'Manager Actions',
      cell: (row) => {
        const isPending = row.status === 'Pending';
        return (
          <div
            className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Quick Preview Eye Button */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleOpenDrawer(row.id)}
              className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 text-foreground hover:text-primary cursor-pointer transition-all shrink-0"
              title="Quick preview details in drawer"
            >
              <Eye className="h-4 w-4" />
            </Button>

            {isPending ? (
              <>
                {/* Dynamic 2-Tier Approval Button */}
                {!row.workflowChain || row.workflowChain.length === 0 || row.workflowChain[0]?.status !== 'Completed' ? (
                  <Button
                    size="sm"
                    onClick={() => handleQuickApprove(row, 1)}
                    disabled={isProcessingAction}
                    className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white gap-1 text-xs font-semibold rounded-lg shadow-xs cursor-pointer inline-flex items-center transition-all"
                    title="Step 1: Department Manager Operational Signoff"
                  >
                    <Check className="h-3.5 w-3.5" />
                    <span>Manager Signoff</span>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleQuickApprove(row, 2)}
                    disabled={isProcessingAction}
                    className="h-8 px-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 active:scale-95 text-white gap-1.5 text-xs font-bold rounded-lg shadow-xs cursor-pointer inline-flex items-center transition-all"
                    title="Step 2: Finance Controller Budget Clearance & SAP PR Generation"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 text-amber-300" />
                    <span>Clear Budget (Finance)</span>
                  </Button>
                )}

                {/* Quick Reject Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenRejectModal(row)}
                  disabled={isProcessingAction}
                  className="h-8 px-2.5 border-rose-300 dark:border-rose-800/60 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 active:scale-95 gap-1 text-xs font-semibold rounded-lg shadow-2xs cursor-pointer inline-flex items-center transition-all"
                  title="Reject this requisition with reason"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  <span>Reject</span>
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(ROUTES.APPROVALS.DETAILS(row.id))}
                className="h-8 px-3 text-xs font-semibold rounded-lg border-border text-foreground hover:bg-muted font-poppins cursor-pointer shadow-xs inline-flex items-center gap-1"
                title="View full authorization record"
              >
                <span>View Details</span>
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <ShieldCheck className="h-7 w-7 text-primary" /> Approval Control Center
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Authoritative executive decision workspace for managers, finance controllers, and procurement heads.
          </p>
        </div>
      </div>

      {/* Squeezed Executive Filters & Search Card */}
      <Card className="rounded-2xl border border-border shadow-xs overflow-x-hidden font-poppins">
        <CardHeader className="pb-4 border-b border-border space-y-3">
          {/* Top Row: Search Input & Total Count */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search REQ #, title, requester, or department..."
                className="w-full pl-9 pr-8 py-2 bg-surface border border-border rounded-xl text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all font-medium font-poppins"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground cursor-pointer"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <span className="text-xs text-foreground-muted font-bold font-poppins">
                {items.length} Requisitions in Queue
              </span>
              <Button variant="ghost" size="sm" onClick={loadData} title="Refresh Table" className="cursor-pointer">
                <RefreshCw className="h-3.5 w-3.5 text-foreground-secondary" />
              </Button>
            </div>
          </div>

          {/* Bottom Row: Squeezed Poppins Segmented Pills & Clear Filters */}
          <div className="flex flex-wrap items-center gap-2 pt-1 font-poppins">
            {/* Status Pills with Sliding Framer Motion Highlight */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border shrink-0 font-poppins">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setActiveTab(tab.value)}
                    className={`relative px-3 py-1.5 rounded-lg text-xs font-poppins font-medium transition-colors whitespace-nowrap cursor-pointer z-10 ${
                      isActive
                        ? 'text-primary-foreground font-semibold'
                        : 'text-foreground-secondary hover:text-foreground'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeApprovalPill"
                        className="absolute inset-0 rounded-lg bg-primary shadow-xs -z-10"
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                      />
                    )}
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Clear Filters Option Pill */}
            <AnimatePresence>
              {isFiltered && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8, x: -6 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8, x: -6 }}
                  transition={{ duration: 0.15 }}
                  type="button"
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 rounded-lg text-xs font-poppins font-bold text-rose-600 dark:text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/25 flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap shadow-2xs shrink-0"
                  title="Reset all filters and search"
                >
                  <RotateCcw className="h-3.5 w-3.5 stroke-[2.5]" />
                  <span>Clear Filters</span>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </CardHeader>

        {/* Fixed Non-Moving Table with Sticky Header */}
        <CardContent className="p-4 overflow-x-hidden">
          <DataTable
            data={items}
            columns={columns}
            isLoading={isLoading}
            hideSearch={true}
            fixedHeight={true}
            containerHeightClass="max-h-[560px] overflow-y-auto overflow-x-hidden"
            emptyTitle="No Approvals Found"
            emptyDescription="There are no pending procurement authorizations matching your current filters."
            onClearFilters={handleClearFilters}
          />
        </CardContent>
      </Card>

      {/* Slide-Over Quick Review Drawer */}
      <SlideOverDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={selectedDetail ? `Review: ${selectedDetail.relatedEntityId || selectedDetail.approvalNumber}` : 'Requisition Review'}
      >
        {isDrawerLoading ? (
          <div className="py-16 flex flex-col items-center justify-center space-y-3">
            <RefreshCw className="h-8 w-8 text-primary animate-spin" />
            <p className="text-xs text-foreground-muted font-medium">Loading requisition authorization data...</p>
          </div>
        ) : selectedDetail ? (
          <div className="space-y-6 font-poppins">
            {/* Header Status Bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-muted/40 rounded-xl border border-border">
              <div className="flex items-center gap-2">
                <StatusBadge status={selectedDetail.status} category="PurchaseRequest" className="font-poppins" />
                <Badge variant="outline" className="text-[10px] font-bold">
                  {selectedDetail.approvalType.replace('_', ' ')}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-bold">
                  {selectedDetail.priority} PRIORITY
                </Badge>
              </div>
              <span className="text-xs font-mono font-bold text-foreground-muted">
                Task ID: {selectedDetail.approvalNumber}
              </span>
            </div>

            {/* Title & Description */}
            <div>
              <h2 className="text-base font-bold text-foreground leading-snug">{selectedDetail.title}</h2>
              <p className="text-xs text-foreground-secondary mt-1">
                Submitted on {new Date(selectedDetail.submittedDate).toLocaleDateString()}
              </p>
            </div>

            {/* 4-Tile Metadata Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-surface border border-border rounded-xl space-y-1">
                <div className="flex items-center gap-1.5 text-foreground-muted text-[11px] font-semibold">
                  <User className="h-3.5 w-3.5 text-primary" /> Requester
                </div>
                <div className="text-xs font-bold text-foreground truncate">{selectedDetail.requesterName}</div>
                <div className="text-[10px] text-foreground-secondary truncate">{selectedDetail.departmentName}</div>
              </div>

              <div className="p-3 bg-surface border border-border rounded-xl space-y-1">
                <div className="flex items-center gap-1.5 text-foreground-muted text-[11px] font-semibold">
                  <Building2 className="h-3.5 w-3.5 text-primary" /> Cost Center & Plant
                </div>
                <div className="text-xs font-bold text-foreground font-mono truncate">{selectedDetail.costCenter}</div>
                <div className="text-[10px] text-foreground-secondary truncate">{selectedDetail.plantName}</div>
              </div>

              <div className="p-3 bg-surface border border-border rounded-xl space-y-1">
                <div className="flex items-center gap-1.5 text-foreground-muted text-[11px] font-semibold">
                  <Clock className="h-3.5 w-3.5 text-amber-500" /> SLA Horizon
                </div>
                <div className="text-xs font-bold text-foreground">
                  {selectedDetail.slaHoursRemaining}h remaining
                </div>
                <div className="text-[10px] text-foreground-secondary truncate">{selectedDetail.currentStage}</div>
              </div>

              <div className="p-3 bg-surface border border-border rounded-xl space-y-1">
                <div className="flex items-center gap-1.5 text-foreground-muted text-[11px] font-semibold">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Total Estimated Value
                </div>
                <div className="text-xs font-extrabold text-foreground font-mono">
                  ₹{selectedDetail.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">
                  {selectedDetail.capexOpex} Budget Available
                </div>
              </div>
            </div>

            {/* Business Justification */}
            <div className="p-3.5 bg-muted/30 border border-border rounded-xl space-y-1">
              <span className="text-[11px] font-bold text-foreground-secondary uppercase tracking-wider block">
                Business Justification
              </span>
              <p className="text-xs text-foreground leading-relaxed italic">
                "{selectedDetail.businessJustification}"
              </p>
            </div>

            {/* Line Items Preview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-primary" /> Requisition Items ({selectedDetail.items.length})
                </span>
                <span className="text-[10px] font-mono text-foreground-muted">
                  Currency: {selectedDetail.currency}
                </span>
              </div>

              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {selectedDetail.items.map((item, idx) => (
                  <div key={item.id || idx} className="p-2.5 bg-surface border border-border rounded-xl text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-foreground truncate max-w-[200px]">{item.description}</span>
                      <span className="font-mono font-extrabold text-foreground">
                        ₹{item.totalAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-foreground-muted">
                      <span>Code: {item.itemCode}</span>
                      <span>
                        Qty: {item.quantity} {item.uom} @ ₹{item.unitPrice.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Approval Workflow Chain */}
            <div className="space-y-2">
              <span className="text-xs font-bold text-foreground block">Approval Stage Sequence</span>
              <div className="space-y-1.5">
                {selectedDetail.workflowChain.map((step) => (
                  <div
                    key={step.stepNumber}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/40 border border-border text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-[10px] flex items-center justify-center">
                        {step.stepNumber}
                      </span>
                      <div>
                        <span className="font-bold text-foreground block">{step.stageName}</span>
                        <span className="text-[10px] text-foreground-muted">{step.assignedApproverName}</span>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[9px] font-bold ${
                        step.status === 'Completed'
                          ? 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10'
                          : 'border-amber-500/30 text-amber-600 dark:text-amber-400 bg-amber-500/10'
                      }`}
                    >
                      {step.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Bar inside Drawer */}
            <div className="pt-4 border-t border-border flex flex-col gap-2">
              {selectedDetail.status === 'Pending' ? (
                <div className="flex items-center gap-2">
                  {!selectedDetail.workflowChain || selectedDetail.workflowChain.length === 0 || selectedDetail.workflowChain[0]?.status !== 'Completed' ? (
                    <Button
                      onClick={() => handleQuickApprove(selectedDetail, 1)}
                      disabled={isProcessingAction}
                      className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs gap-1.5 cursor-pointer shadow-xs"
                    >
                      <Check className="h-4 w-4" /> Manager Signoff (Step 1/2)
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleQuickApprove(selectedDetail, 2)}
                      disabled={isProcessingAction}
                      className="flex-1 h-9 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-xs gap-1.5 cursor-pointer shadow-xs"
                    >
                      <ShieldCheck className="h-4 w-4 text-amber-300" /> Clear Budget & Generate SAP PR (Step 2/2)
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => handleOpenRejectModal(selectedDetail)}
                    disabled={isProcessingAction}
                    className="h-9 border-rose-300 dark:border-rose-800 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 font-bold text-xs gap-1.5 cursor-pointer"
                  >
                    <XCircle className="h-4 w-4" /> Reject
                  </Button>
                </div>
              ) : (
                <div className="p-2.5 rounded-xl bg-muted text-center text-xs font-bold text-foreground-secondary">
                  This requisition has been marked as {selectedDetail.status}.
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsDrawerOpen(false);
                  navigate(ROUTES.APPROVALS.DETAILS(selectedDetail.id));
                }}
                className="w-full text-xs text-primary font-bold hover:underline gap-1.5 cursor-pointer"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open Full Decision Workspace
              </Button>
            </div>
          </div>
        ) : null}
      </SlideOverDrawer>

      {/* Reject Confirmation Modal */}
      <AnimatePresence>
        {isRejectModalOpen && rejectTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-xs"
              onClick={() => !isProcessingAction && setIsRejectModalOpen(false)}
            />

            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 12 }}
              transition={{ type: 'spring', stiffness: 450, damping: 30 }}
              className="relative w-full max-w-md bg-surface rounded-2xl border border-border shadow-2xl p-6 space-y-4 z-10 font-poppins"
            >
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400 shrink-0">
                  <XCircle className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">
                    Reject Requisition: {rejectTarget.relatedEntityId || rejectTarget.approvalNumber}
                  </h3>
                  <p className="text-xs text-foreground-muted mt-0.5">
                    Please specify a mandatory justification reason for rejecting "{rejectTarget.title}". This will be logged in the SOX audit trail.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground flex items-center gap-1">
                  Rejection Reason <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g. Total estimated cost exceeds allocated departmental budget; please re-evaluate line items with catalog alternatives."
                  rows={3}
                  className="w-full p-3 text-xs bg-muted/30 border border-border rounded-xl text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-rose-500/40 font-medium font-poppins resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsRejectModalOpen(false)}
                  disabled={isProcessingAction}
                  className="h-9 px-4 text-xs font-semibold rounded-xl border-border cursor-pointer"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleConfirmReject}
                  disabled={isProcessingAction || !rejectReason.trim()}
                  className="h-9 px-4 text-xs font-bold rounded-xl bg-rose-600 hover:bg-rose-700 text-white cursor-pointer shadow-xs disabled:opacity-50"
                >
                  {isProcessingAction ? 'Rejecting...' : 'Confirm Rejection'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
